// Buy-down "pricing exception" (formerly the bespoke Discount Waiver feature
// at /online-sizer/waiver/*) migrated onto the shared cross-platform waiver
// service — the same backend/API the eligibility-test and General Waiver
// features already use (see src/components/waivers/utils.ts), just with
// waiverType "PricingException" instead of "EvaluationResult"/"GeneralWaiver".
//
// The waiver service does support a delete (DELETE /waiverservice/waiver/
// :waiverId, soft delete — waiver service API §1.7), so re-requesting a
// still-pending exception mirrors the old bespoke flow again: delete the
// pending record outright (deletePricingException below), then create a
// fresh one (createPricingException) once the new form is submitted. A
// decided (approved/rejected) record is never deleted — approve/reject post
// "APPROVED"/"REJECTED" responses onto it and it's kept as history.
//
// calculateCurrentSelectedInterestRate (utils/Common.ts), applyWaiverStatusChip
// and the loan-submission warning (LoansPagev2.tsx), and term sheet generation
// (components/termSheet/Common.ts via useTermSheetActions.ts) all consume the
// *old* bespoke response shape (`{ data: { data: LegacyPricingExceptionDoc[] } }`).
// That shape encodes real, already-tested pricing-calculation behavior, so
// fetchPricingExceptionsForLoan below deliberately keeps producing it —
// only the network call underneath actually moves to the waiver service.
import {
  createWaiversInCommonService,
  updateWaiversInCommonService,
  fetchWaiversInCommonService,
  deleteWaiverInCommonService,
  getWaiverDocumentUploadUrl,
  uploadWaiverDocumentToGcs,
  attachWaiverDocumentPaths,
} from "../services/network";
import {
  WAIVER_SERVICE_SOURCE_PLATFORM,
  WaiverServiceRecord,
  WaiverServiceResponse,
  downloadWaiverDocument,
} from "../components/waivers/utils";

export const PRICING_EXCEPTION_TYPE = "PricingException";
// One pricing-exception record per loan — same "single active ask" rule the
// old bespoke flow enforced via its re-request confirmation modal.
export const PRICING_EXCEPTION_KEY = "PRICING_EXCEPTION";

const PRICING_EXCEPTION_RESPONSE_TYPE = {
  REQUESTED: "REQUESTED",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
} as const;

// Sent on every pricing-exception response (request, re-request, approve,
// reject alike) — the waiver service uses it to populate the notification
// email sent to the requester when the exception is decided. Built by the
// caller (RequestWaiverWrapper.tsx has the loan/context data this needs;
// this module doesn't) and passed straight through.
export interface PricingExceptionEmailRequestBody {
  orgId?: string;
  evaluationId?: string;
  originalLoanAmount?: string;
  propertyAddress?: string;
  originationPoints?: string;
  totalBuyYield?: string;
  createdByEmail?: string;
}

export interface LegacyPricingExceptionDoc {
  _id: string;
  data: {
    interestRate?: string;
    requestedData: {
      discountPoints?: string;
      discountPointsAmount?: string;
      interestRate?: string;
      comments?: string;
      userName?: string;
      createdOn?: string;
    };
    actualData: {
      discountPoints?: string;
      discountPointsAmount?: string;
      interestRate?: string;
    };
    finalData?: {
      discountPoints?: string;
      discountPointsAmount?: string;
      interestRate?: string;
      comments?: string;
      userName?: string;
      createdOn?: string;
    };
    isActive: boolean;
    waiverStatus: "Requested" | "Approved" | "Rejected";
    fileLocation?: string;
    fileName?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

// Finds the buy-down option this exception was raised against, purely from
// the loan's currently-loaded buyDownOptions and the rate the record was
// opened at — the "actual" (pre-exception) discount/rate isn't persisted on
// the record itself, since it's always fully derivable this way, the same
// lookup the old bespoke flow did at request/approval time.
const findBuyDownOptionForRate = (buyDownOptions: any[] | undefined, interestRateLabel: string | undefined) => {
  if (!interestRateLabel) return undefined;
  const target = parseFloat(interestRateLabel.replace("%", ""));
  // Rounded string comparison, not raw ===  — buyDownOptions' interestRate is
  // a fraction (e.g. 0.06375) computed by the rate-sheet evaluation via a
  // different arithmetic path than this string-parsed percentage, so a
  // strict floating-point equality check essentially never matches (this is
  // the same pattern LoansPagev2.tsx already uses for the identical
  // comparison, e.g. its own buyDownOptions?.find by interestRate).
  return buyDownOptions?.find(
    (option: any) => (option?.interestRate * 100)?.toFixed(3) === target?.toFixed(3),
  );
};

const getFirstDocumentPath = (waiver: WaiverServiceRecord): string | undefined => waiver.documentPaths?.[0];
const getFirstDocumentName = (waiver: WaiverServiceRecord): string | undefined =>
  getFirstDocumentPath(waiver)?.split("/")?.pop();

const toLegacyActualData = (waiver: WaiverServiceRecord, buyDownOptions: any[] | undefined) => {
  const option = findBuyDownOptionForRate(buyDownOptions, waiver.interestRate);
  return {
    interestRate: waiver.interestRate,
    discountPoints:
      option?.discountPointPercentage != null ? `${parseFloat(option.discountPointPercentage).toFixed(4)}` : undefined,
    discountPointsAmount:
      option?.discountPointAmount != null ? `${parseFloat(option.discountPointAmount).toFixed(2)}` : undefined,
  };
};

// requestedResponse is the REQUESTED response this ask/re-ask corresponds
// to — undefined for the very first ask, whose numbers live on the record
// itself (set at creation time) rather than as a response.
const toLegacyRequestedData = (waiver: WaiverServiceRecord, requestedResponse: WaiverServiceResponse | undefined) => ({
  discountPoints: requestedResponse?.discountPointPercentage ?? waiver.requestedDiscountPointPercentage,
  discountPointsAmount: requestedResponse?.discountPointAmount ?? waiver.requestedDiscountPointAmount,
  interestRate: waiver.interestRate,
  comments: requestedResponse?.comments ?? waiver.description,
  userName: requestedResponse?.responseBy ?? waiver.createdBy,
  createdOn: requestedResponse?.responseDate ?? waiver.createdOn,
});

// The underwriter's actual decision comment doesn't reliably land in the
// APPROVED/REJECTED response's own .comments field — confirmed on real
// records: it instead shows up as a waiverChat chat line tagged with the
// decision, e.g. { tags: ["Approve"], lineText: "testing the approver flow" }
// or { tags: ["Reject"], lineText: "..." } — both the present-tense verb
// form, confirmed against real waiver-service data rather than inferred from
// the APPROVED/REJECTED responseType strings.
const DECISION_CHAT_TAG: Record<string, string> = {
  [PRICING_EXCEPTION_RESPONSE_TYPE.APPROVED]: "Approve",
  [PRICING_EXCEPTION_RESPONSE_TYPE.REJECTED]: "Reject",
};

// Picks the most recent chat line tagged with the given decision, in case a
// record somehow ends up with more than one.
const findChatLineByTag = (waiver: WaiverServiceRecord, tag: string): string | undefined => {
  const matches = (waiver.waiverChat?.chatLines ?? []).filter((chatLine) =>
    chatLine.tags?.includes(tag),
  );
  return matches[matches.length - 1]?.lineText;
};

const toLegacyFinalData = (waiver: WaiverServiceRecord, decisionResponse: WaiverServiceResponse) => {
  const chatTag = decisionResponse.responseType
    ? DECISION_CHAT_TAG[decisionResponse.responseType]
    : undefined;
  return {
    discountPoints: decisionResponse.discountPointPercentage,
    discountPointsAmount: decisionResponse.discountPointAmount,
    // The interest rate a record was opened against never changes across its
    // responses — neither ApprovalFlow nor RejectionFlow ever offer to edit it.
    interestRate: waiver.interestRate,
    comments: chatTag
      ? findChatLineByTag(waiver, chatTag) ?? decisionResponse.comments
      : decisionResponse.comments,
    userName: decisionResponse.responseBy,
    createdOn: decisionResponse.responseDate,
  };
};

// Reconstructs the old bespoke flow's "one document per request cycle" view
// from a single record's append-only response log: every REQUESTED response
// immediately followed by a decision becomes one history entry (isActive
// false); whatever's left un-decided (a dangling REQUESTED response, or no
// responses at all yet) becomes the single active entry (isActive true) —
// there's at most one of those per record, same as the old "only one active
// waiver at a time" rule.
export const mapPricingExceptionRecordToLegacyDocs = (
  waiver: WaiverServiceRecord,
  buyDownOptions: any[] | undefined,
  // Backend gap: waiver.interestRate isn't currently persisted/returned on
  // the record at all, which breaks actualData's buyDownOptions lookup (and
  // the Requested/Final rate display) entirely. The rate is fixed for a
  // record's whole lifetime — never re-editable once raised, confirmed by
  // product — so this same fallback applies uniformly to every doc derived
  // from this record (pending AND history/decided), not just the active
  // one: restricting it to "active only" meant everything interestRate-
  // dependent went blank the instant a decision was recorded, since the
  // record immediately moves into the no-fallback history path. The
  // remaining, unavoidable risk is a genuinely OLD record (raised in an
  // earlier session where a different rate was selected) showing today's
  // selected rate instead of its own — but with the backend not persisting
  // the real value at all, that's still strictly better than blank.
  fallbackInterestRate?: string,
): LegacyPricingExceptionDoc[] => {
  const responses = waiver.waiverResponses ?? [];
  const effectiveWaiver = waiver.interestRate
    ? waiver
    : { ...waiver, interestRate: fallbackInterestRate };
  const actualData = toLegacyActualData(effectiveWaiver, buyDownOptions);
  const docs: LegacyPricingExceptionDoc[] = [];

  let pendingRequested: WaiverServiceResponse | undefined;
  responses.forEach((response) => {
    if (response.responseType === PRICING_EXCEPTION_RESPONSE_TYPE.REQUESTED) {
      pendingRequested = response;
    } else if (
      response.responseType === PRICING_EXCEPTION_RESPONSE_TYPE.APPROVED ||
      response.responseType === PRICING_EXCEPTION_RESPONSE_TYPE.REJECTED
    ) {
      docs.push({
        _id: waiver.waiverId ?? "",
        data: {
          interestRate: effectiveWaiver.interestRate,
          requestedData: toLegacyRequestedData(effectiveWaiver, pendingRequested),
          actualData,
          finalData: toLegacyFinalData(effectiveWaiver, response),
          isActive: false,
          waiverStatus: response.responseType === PRICING_EXCEPTION_RESPONSE_TYPE.APPROVED ? "Approved" : "Rejected",
          fileLocation: getFirstDocumentPath(waiver),
          fileName: getFirstDocumentName(waiver),
        },
        createdAt: pendingRequested?.responseDate ?? waiver.createdOn,
        updatedAt: response.responseDate,
      });
      pendingRequested = undefined;
    }
  });

  const isStillPending = !!pendingRequested || responses.length === 0;
  if (isStillPending) {
    docs.push({
      _id: waiver.waiverId ?? "",
      data: {
        interestRate: effectiveWaiver.interestRate,
        requestedData: toLegacyRequestedData(effectiveWaiver, pendingRequested),
        actualData,
        isActive: true,
        waiverStatus: "Requested",
        fileLocation: getFirstDocumentPath(waiver),
        fileName: getFirstDocumentName(waiver),
      },
      createdAt: pendingRequested?.responseDate ?? waiver.createdOn,
    });
  }

  return docs;
};

// Fetches every EXCEPTION-type record for this loan from the waiver service
// and reshapes them into the exact `{ data: { data: [...] } }` axios-response
// shape the old /online-sizer/waiver/fetch endpoint returned — so
// calculateCurrentSelectedInterestRate, applyWaiverStatusChip, and term sheet
// generation (all written against that shape) keep working unchanged.
export const fetchPricingExceptionsForLoan = async (
  loanId: string | undefined,
  buyDownOptions?: any[],
  fallbackInterestRate?: string,
): Promise<{ data: { data: LegacyPricingExceptionDoc[] } }> => {
  if (!loanId) return { data: { data: [] } };
  const { data } = await fetchWaiversInCommonService(loanId, loanId);
  const records: WaiverServiceRecord[] = Array.isArray(data) ? data : (data?.data ?? []);
  const docs = records
    .filter((record) => record.waiverType === PRICING_EXCEPTION_TYPE)
    .flatMap((record) => mapPricingExceptionRecordToLegacyDocs(record, buyDownOptions, fallbackInterestRate));
  return { data: { data: docs } };
};

// Builds the create-waiver request body (POST /waiverservice/LOAN/{loanId}/waiver).
// Always created with no documents, same reasoning as the eligibility-test
// waiver's buildWaiverCreatePayload: the document-upload endpoints require an
// existing waiverId, so a selected file is attached afterward via
// uploadPricingExceptionDocument once this call has returned one.
export const buildPricingExceptionCreatePayload = (
  loanId: string | undefined,
  request: {
    interestRate?: string;
    discountPoints?: string;
    discountPointsAmount?: string;
    comments?: string;
  },
  // Caller-resolved (RequestWaiverWrapper.tsx): the loan's assigned partner,
  // falling back to the first eligible partner when none is assigned yet —
  // same fallback the eligibility-test waiver's takeoutPartnerId doesn't
  // need (a test waiver only ever comes from BasicCustomCard, which renders
  // per-partner once one's assigned), but a pricing exception can be
  // requested before a partner is picked.
  takeoutPartnerId?: string | null,
) => ({
  waiverType: PRICING_EXCEPTION_TYPE,
  waiverKeys: [PRICING_EXCEPTION_KEY],
  // Same secondaryIdentifier as the eligibility-test/General Waiver create
  // payload (buildWaiverCreatePayload) — every waiver on this loan stage is
  // "FES" today, sizer has no other loan stage concept yet.
  secondaryIdentifierName: "LOAN_STAGE",
  secondaryIdentifierValue: "FES",
  title: "Pricing Exception Request",
  description: request.comments ?? "",
  interestRate: request.interestRate,
  requestedDiscountPointPercentage: request.discountPoints,
  requestedDiscountPointAmount: request.discountPointsAmount,
  takeoutPartnerId: takeoutPartnerId ?? null,
  sizerLoanId: loanId,
  sourcePlatform: WAIVER_SERVICE_SOURCE_PLATFORM,
  taggedPartyIds: [],
  documentPaths: [],
  // The service's DTO requires this field to be present (isDefined) even
  // though pricing exceptions don't collect compensating factors the way
  // eligibility-test waivers do — an empty array satisfies the validator
  // without implying any factors were actually selected.
  waiverMitigates: [],
  waiverInfo: {
    hasDocuments: false,
    hasDiscussions: false,
    isInternal: false,
    finalResponse: ""
  },
});

// Uploads a single file to a presigned per-file GCS URL scoped to this
// waiver, then records its path on the waiver (§1.11) — usable any time
// after the waiver exists, including attaching a new document on a
// re-request, not just at initial creation.
export const uploadPricingExceptionDocument = async (waiverId: string, file: File): Promise<string> => {
  const { data } = await getWaiverDocumentUploadUrl(waiverId, file.name);
  await uploadWaiverDocumentToGcs(file, data?.uploadUrl);
  await attachWaiverDocumentPaths(waiverId, [data?.path]);
  return data?.path;
};

// Creates the waiver (§1.1), then immediately posts an explicit "REQUESTED"
// response (§2.1.3) recording the ask — the initial request is a real
// response entry from the start, same as every later re-request/decision,
// rather than being left implicit from the record's own top-level fields.
export const createPricingException = async (
  loanId: string | undefined,
  responseBy: string | undefined,
  request: {
    interestRate?: string;
    discountPoints?: string;
    discountPointsAmount?: string;
    comments?: string;
    emailRequestBody?: PricingExceptionEmailRequestBody;
  },
  file?: File | null,
  // Caller-resolved (RequestWaiverWrapper.tsx): assignedPartner, falling
  // back to the first eligible partner — see buildPricingExceptionCreatePayload.
  takeoutPartnerId?: string | null,
): Promise<WaiverServiceRecord> => {
  const payload = buildPricingExceptionCreatePayload(loanId, request, takeoutPartnerId);
  const { data } = await createWaiversInCommonService(payload, loanId ?? "");
  let record: WaiverServiceRecord = { ...payload, waiverResponses: [], ...(data ?? {}) };

  if (record.waiverId) {
    const requestedResponse = buildPricingExceptionResponsePayload(
      PRICING_EXCEPTION_RESPONSE_TYPE.REQUESTED,
      responseBy,
      request,
    );
    await updateWaiversInCommonService(requestedResponse, record.waiverId);
    record = { ...record, waiverResponses: [...(record.waiverResponses ?? []), requestedResponse] };
  }

  if (file && record.waiverId) {
    const path = await uploadPricingExceptionDocument(record.waiverId, file);
    record = { ...record, documentPaths: [...(record.documentPaths ?? []), path] };
  }

  return record;
};

const buildPricingExceptionResponsePayload = (
  responseType: (typeof PRICING_EXCEPTION_RESPONSE_TYPE)[keyof typeof PRICING_EXCEPTION_RESPONSE_TYPE],
  responseBy: string | undefined,
  fields: {
    discountPoints?: string;
    discountPointsAmount?: string;
    comments?: string;
    emailRequestBody?: PricingExceptionEmailRequestBody;
  },
): WaiverServiceResponse => ({
  responseType,
  responseBy: responseBy || "unknown",
  responseDate: new Date().toISOString(),
  isApproved: responseType === PRICING_EXCEPTION_RESPONSE_TYPE.APPROVED,
  compensatingFactors: [],
  discountPointPercentage: fields.discountPoints,
  discountPointAmount: fields.discountPointsAmount,
  comments: fields.comments ?? "",
  emailRequestBody: fields.emailRequestBody,
});

// Not the primary re-request path anymore — RequestWaivers.tsx now deletes
// the pending record outright (deletePricingException) when the user
// confirms a re-request, then createPricingException makes a fresh one on
// submit. Kept as a fallback for posting a REQUESTED response onto an
// existing record directly, without a delete.
export const reRequestPricingException = (
  waiverId: string,
  responseBy: string | undefined,
  fields: {
    discountPoints?: string;
    discountPointsAmount?: string;
    comments?: string;
    emailRequestBody?: PricingExceptionEmailRequestBody;
  },
) =>
  updateWaiversInCommonService(
    buildPricingExceptionResponsePayload(PRICING_EXCEPTION_RESPONSE_TYPE.REQUESTED, responseBy, fields),
    waiverId,
  );

// Deletes a still-pending pricing exception outright (soft delete on the
// waiver service) — used when the user confirms replacing it with a fresh
// request via ReRequestWaiverConfirmation, rather than appending a new
// response onto the old record.
export const deletePricingException = (waiverId: string) =>
  deleteWaiverInCommonService(waiverId);

export const approvePricingException = (
  waiverId: string,
  responseBy: string | undefined,
  fields: {
    discountPoints?: string;
    discountPointsAmount?: string;
    comments?: string;
    emailRequestBody?: PricingExceptionEmailRequestBody;
  },
) =>
  updateWaiversInCommonService(
    buildPricingExceptionResponsePayload(PRICING_EXCEPTION_RESPONSE_TYPE.APPROVED, responseBy, fields),
    waiverId,
  );

export const rejectPricingException = (
  waiverId: string,
  responseBy: string | undefined,
  fields: {
    discountPoints?: string;
    discountPointsAmount?: string;
    comments?: string;
    emailRequestBody?: PricingExceptionEmailRequestBody;
  },
) =>
  updateWaiversInCommonService(
    buildPricingExceptionResponsePayload(PRICING_EXCEPTION_RESPONSE_TYPE.REJECTED, responseBy, fields),
    waiverId,
  );

export const downloadPricingExceptionDocument = downloadWaiverDocument;
